import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogOut, Save, Users, ArrowLeft, ToggleLeft } from "lucide-react";
import { toast } from "sonner";
import { api, authHeader } from "@/lib/api";
import QuizzyLogo from "@/components/QuizzyLogo";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("quizzy_admin_token"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);

  const logout = () => {
    localStorage.removeItem("quizzy_admin_token");
    setToken(null);
  };

  useEffect(() => {
    if (!token) return;
    Promise.all([
      api.get("/settings"),
      api.get("/admin/subscribers", { headers: authHeader() }),
    ])
      .then(([s, sub]) => {
        setSettings(s.data);
        setSubs(sub.data.subscribers);
      })
      .catch((e) => {
        if (e.response?.status === 401) logout();
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { password });
      localStorage.setItem("quizzy_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Wrong password. Try again.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings, { headers: authHeader() });
      setSettings(data);
      toast.success("Settings saved.");
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className="grid-lines flex min-h-screen items-center justify-center bg-[#08090E] px-4">
        <motion.div
          data-testid="admin-login-modal"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="glass w-full max-w-sm rounded-3xl p-8"
        >
          <div className="flex items-center gap-3">
            <QuizzyLogo size={38} />
            <div>
              <p className="font-display text-sm font-bold text-white">QUIZZY CONTROL</p>
              <p className="font-code text-[10px] tracking-widest text-[#64748B]">ADMIN ACCESS</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-7 space-y-4">
            <div className="flex items-center gap-3 rounded-full border border-[#222738] bg-[#0F111A] px-5 focus-within:border-[#00F0FF]">
              <Lock size={14} className="text-[#64748B]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                data-testid="admin-password-input"
                className="w-full bg-transparent py-3.5 font-sub text-sm text-white placeholder:text-[#64748B] focus:outline-none"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="font-code text-xs text-[#FF007A]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="btn-neon w-full rounded-full bg-[#00F0FF] py-3.5 font-sub text-sm font-bold text-[#08090E]"
            >
              Enter Control Room
            </button>
          </form>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 font-code text-[10px] tracking-widest text-[#64748B] hover:text-[#00F0FF]">
            <ArrowLeft size={11} /> BACK TO SITE
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08090E] px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QuizzyLogo size={38} />
            <div>
              <p className="font-display text-sm font-bold text-white">QUIZZY CONTROL</p>
              <p className="font-code text-[10px] tracking-widest text-[#64748B]">ADMIN PANEL</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="rounded-full border border-[#222738] px-4 py-2 font-sub text-xs font-semibold text-[#94A3B8] hover:border-[#00F0FF] hover:text-white"
            >
              View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-full border border-[#222738] px-4 py-2 font-sub text-xs font-semibold text-[#94A3B8] hover:border-[#FF007A] hover:text-[#FF007A]"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {settings && (
          <div className="glass mt-10 rounded-3xl p-6 sm:p-8">
            <p className="flex items-center gap-2 font-code text-[11px] tracking-[0.3em] text-[#00F0FF]">
              <ToggleLeft size={14} /> SITE MODE
            </p>
            <div className="mt-5 flex items-center justify-between rounded-2xl border border-[#222738] bg-[#0F111A] p-5">
              <div>
                <p className="font-sub text-sm font-semibold text-white">Coming Soon page</p>
                <p className="mt-1 text-xs text-[#64748B]">
                  ON = visitors see only the Coming Soon page. OFF = full site is live.
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, coming_soon: !settings.coming_soon })}
                data-testid="admin-toggle-coming-soon-switch"
                className={`relative h-8 w-14 shrink-0 rounded-full transition-colors duration-300 ${
                  settings.coming_soon ? "bg-[#00F0FF]" : "bg-[#222738]"
                }`}
                aria-label="Toggle coming soon"
              >
                <span
                  className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all duration-300 ${
                    settings.coming_soon ? "left-7" : "left-1"
                  }`}
                />
              </button>
            </div>
            <p
              data-testid="admin-mode-status"
              className={`mt-3 font-code text-[11px] tracking-widest ${
                settings.coming_soon ? "text-[#00F0FF]" : "text-[#39FF14]"
              }`}
            >
              STATUS: {settings.coming_soon ? "COMING SOON ACTIVE" : "SITE IS LIVE"}
            </p>

            <p className="mt-8 font-code text-[11px] tracking-[0.3em] text-[#00F0FF]">
              PLAY STORE LINK
            </p>
            <input
              type="url"
              value={settings.playstore_url}
              onChange={(e) => setSettings({ ...settings, playstore_url: e.target.value })}
              data-testid="admin-playstore-link-input"
              className="mt-3 w-full rounded-2xl border border-[#222738] bg-[#0F111A] px-5 py-3.5 font-code text-sm text-white focus:border-[#00F0FF] focus:outline-none"
            />
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-save-settings-button"
              className="btn-neon mt-6 flex items-center gap-2 rounded-full bg-[#39FF14] px-7 py-3 font-sub text-sm font-bold text-[#08090E] disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}

        <div className="glass mt-8 rounded-3xl p-6 sm:p-8">
          <p className="flex items-center gap-2 font-code text-[11px] tracking-[0.3em] text-[#FF007A]">
            <Users size={14} /> NOTIFY LIST — {subs.length} SUBSCRIBERS
          </p>
          <div data-testid="admin-subscribers-list" className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
            {subs.length === 0 && (
              <p className="text-sm text-[#64748B]">No subscribers yet. Share the link!</p>
            )}
            {subs.map((s) => (
              <div
                key={s.email}
                className="flex items-center justify-between rounded-xl border border-[#222738] bg-[#0F111A] px-4 py-2.5"
              >
                <span className="font-sub text-sm text-white">{s.email}</span>
                <span className="font-code text-[10px] text-[#64748B]">
                  {s.created_at ? s.created_at.slice(0, 10) : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
